export const omit = (keysToOmit = [], originalObj = {}) =>
  Object.fromEntries(
    Object.entries(originalObj).filter(([key]) => !keysToOmit.includes(key))
  );

export const remain = (keysToRemain = [], originalObj = {}) =>
  Object.fromEntries(
    Object.entries(originalObj).filter(([key]) => keysToRemain.includes(key))
  );

export const clean = obj => {
  for (let key in obj) {
    if (obj[key] === null || obj[key] === undefined || obj[key] === "")
      delete obj[key];
  }
  return obj;
};

/**
 * Let's you find an deeply nested object value with a single string key
 * 
 * var someObject = {
    'part1' : {
        'name': 'Part 1',
        'size': '20',
        'qty' : '50'
    },
    'part2' : {
        'name': 'Part 2',
        'size': '15',
        'qty' : '60'
    }}
 * ie. key = "part2.size"
 * 
 * 
 * @param {object} o 
 * @param {string} s 
 * @returns any
 */
export const valueByStringKey = function (o, s) {
  s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
  s = s.replace(/^\./, ""); // strip a leading dot
  const a = s.split(".");
  for (var i = 0; i < a.length; ++i) {
    if (typeof o !== "object" || o === null) return;
    var k = a[i];
    if (k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
};

var hasElementType = typeof Element !== "undefined";
var hasMap = typeof Map === "function";
var hasSet = typeof Set === "function";
var hasArrayBuffer = typeof ArrayBuffer === "function" && !!ArrayBuffer.isView;

// Note: We **don't** need `envHasBigInt64Array` in fde es6/index.js

/**
 * A modified version of the react-fast-compare util
 * this version doesn't conisider array order of appearance,
 * only array contents
 *
 * @param {*} a
 * @param {*} b
 * @returns Boolean
 */
export function equal(a, b) {
  // START: fast-deep-equal es6/index.js 3.1.1
  if (a === b) return true;

  if (a && b && typeof a == "object" && typeof b == "object") {
    if (a.constructor !== b.constructor) return false;

    var length, i, keys;
    if (Array.isArray(a)) {
      length = a.length;
      if (length !== b.length) return false;

      //our modification will sort the arrays before comparison
      //this will remove the requirement that all contents appear in the same order
      a = a.sort((x, y) => (x > y ? 1 : -1));
      b = b.sort((x, y) => (x > y ? 1 : -1));
      for (i = length; i-- !== 0; ) if (!equal(a[i], b[i])) return false;
      return true;
    }

    // START: Modifications:
    // 1. Extra `has<Type> &&` helpers in initial condition allow es6 code
    //    to co-exist with es5.
    // 2. Replace `for of` with es5 compliant iteration using `for`.
    //    Basically, take:
    //
    //    ```js
    //    for (i of a.entries())
    //      if (!b.has(i[0])) return false;
    //    ```
    //
    //    ... and convert to:
    //
    //    ```js
    //    it = a.entries();
    //    while (!(i = it.next()).done)
    //      if (!b.has(i.value[0])) return false;
    //    ```
    //
    //    **Note**: `i` access switches to `i.value`.
    var it;
    if (hasMap && a instanceof Map && b instanceof Map) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done) if (!b.has(i.value[0])) return false;
      it = a.entries();
      while (!(i = it.next()).done)
        if (!equal(i.value[1], b.get(i.value[0]))) return false;
      return true;
    }

    if (hasSet && a instanceof Set && b instanceof Set) {
      if (a.size !== b.size) return false;
      it = a.entries();
      while (!(i = it.next()).done) if (!b.has(i.value[0])) return false;
      return true;
    }
    // END: Modifications

    if (hasArrayBuffer && ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
      length = a.length;
      if (length != b.length) return false;
      for (i = length; i-- !== 0; ) if (a[i] !== b[i]) return false;
      return true;
    }

    if (a.constructor === RegExp)
      return a.source === b.source && a.flags === b.flags;
    if (a.valueOf !== Object.prototype.valueOf)
      return a.valueOf() === b.valueOf();
    if (a.toString !== Object.prototype.toString)
      return a.toString() === b.toString();

    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) return false;

    for (i = length; i-- !== 0; )
      if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
    // END: fast-deep-equal

    // START: react-fast-compare
    // custom handling for DOM elements
    if (hasElementType && a instanceof Element) return false;

    // custom handling for React/Preact
    for (i = length; i-- !== 0; ) {
      if (
        (keys[i] === "_owner" || keys[i] === "__v" || keys[i] === "__o") &&
        a.$$typeof
      ) {
        // React-specific: avoid traversing React elements' _owner
        // Preact-specific: avoid traversing Preact elements' __v and __o
        //    __v = $_original / $_vnode
        //    __o = $_owner
        // These properties contain circular references and are not needed when
        // comparing the actual elements (and not their owners)
        // .$$typeof and ._store on just reasonable markers of elements

        continue;
      }

      // all other properties should be traversed as usual
      if (!equal(a[keys[i]], b[keys[i]])) return false;
    }
    // END: react-fast-compare

    // START: fast-deep-equal
    return true;
  }

  return a !== a && b !== b;
}
// end fast-deep-equal

/**
 * Tests a variable to see if it is a JS Object - {}
 *
 *
 * @param {*} obj
 * @returns Boolean
 */
export function isObject(obj) {
  return Object.prototype.toString.call(obj) === "[object Object]";
}

/**
 *
 * Takes an input of an array of objects, and returns a csv of the object data
 * 'includeFields' param is an array of object key names to include in the csv
 *
 * @param {Object} input
 * @param {Array} includeFields
 * @returns String
 */
export function arrayOfObjectsToCSV(input, includeFields = null) {
  let result = input.map(row => {
    return Array.isArray(includeFields)
      ? Object.entries(row)
          .filter(val => includeFields.includes(val[0]))
          .map(val => val[1])
          .join(",")
      : Object.values(row).join(",");
  });

  return result.join("\n");
}

/**
 * Prompts the browser to download filecontents
 *
 * @param {string} filetype
 * @param {string} filecontents
 * @param {string} filename
 */
export function downloadContentsAsFile(
  filetype = "csv",
  filecontents = "",
  filename
) {
  let header = "";
  switch (filetype) {
    case "csv":
      header = "data:text/csv;charset=utf-8,";
      break;
    case "txt":
      header = "data:text/plain;charset=utf-8,";
      break;
    default:
      break;
  }

  const endcodedContents = encodeURI(header + filecontents);
  const link = document.createElement("a");
  link.setAttribute("href", endcodedContents);
  link.setAttribute("download", filename);
  document.body.appendChild(link); // Required for FF
  link.click(); // This will download the data file named {filename}}.
}

export function stringIsJSON(text) {
  if (typeof text !== "string") {
    return false;
  }
  try {
    var json = JSON.parse(text);
    return typeof json === "object";
  } catch (error) {
    return false;
  }
}
